import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";

import CustomButton from "../../Components/CustomButton";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import ImageViewer from "../../Components/ImageViewer";
import Loader from '../../Components/Loader';
import CustomTable from "./../../Components/CustomTable";
import ProfileDisplay from "../../Components/ProfileDisplay";
import usePagination from "../../Hooks/usePagination";
import useSearch from "../../Hooks/useSearch";
import { useGetAllVendorAdsQuery } from "../../Redux/Apis/VendorAds";
import { dateFormatter } from "../../Utils";

import "./style.css";

const AD_SIZE_LABELS = {
  standard: 'Standard (1/8 Page)',
  bronze: 'Bronze (1/4 Page)',
  silver: 'Silver (1/3 Page)',
  gold: 'Gold (1/2 Page)',
  vip: 'VIP',
};

const SECTION_LABELS = {
  pros: 'The Pros',
  barn: 'The Barn',
  calcutta: 'Calcutta',
  greenskeeping: 'Greenskeeping',
};

const LEGACY_APPROVAL_STATUSES = new Set(['pending', 'approved', 'rejected']);

const getEffectiveAdStatus = (ad) => {
  const rawStatus = ad?.status;
  const hasEnded = ad?.endDate ? new Date(ad.endDate) < new Date() : false;

  if (rawStatus === 'cancelled') {
    return 'cancelled';
  }

  if (rawStatus === 'expired' || (rawStatus === 'active' && hasEnded)) {
    return 'expired';
  }

  if (rawStatus === 'active') {
    return 'active';
  }

  if (LEGACY_APPROVAL_STATUSES.has(rawStatus)) {
    return ad?.stripeSubscriptionId && !hasEnded ? 'active' : 'inactive';
  }

  if (rawStatus === 'inactive') {
    return 'inactive';
  }

  return ad?.stripeSubscriptionId && !hasEnded ? 'active' : 'inactive';
};

const getStatusMeta = (ad) => {
  switch (getEffectiveAdStatus(ad)) {
    case 'active':
      return { label: 'live', className: 'bg-success' };
    case 'inactive':
      return { label: 'needs attention', className: 'bg-warning text-dark' };
    case 'expired':
      return { label: 'ended', className: 'bg-secondary' };
    case 'cancelled':
      return { label: 'cancelled', className: 'bg-dark' };
    default:
      return { label: 'needs attention', className: 'bg-warning text-dark' };
  }
};

const sortValues = [
  {
    text: "Business Name",
    value: "businessName",
  },
  {
    text: "Created Date",
    value: "createdAt",
  }
];

const perPageValues = [
  {
    text: "10",
    value: 10,
  },
  {
    text: "20",
    value: 20,
  },
  {
    text: "50",
    value: 50,
  }
];

const VendorAds = () => {
  const [selectedAd, setSelectedAd] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [sortBy, setSortBy] = useState(sortValues[1].value);

  const [search, debouncedSearch, onSearch] = useSearch();
  const { page, perPage, onNext, onPrevious } = usePagination();

  // Fetch all vendor ads
  const { data, isLoading, isFetching } = useGetAllVendorAdsQuery({
    page,
    limit: perPage,
    search: debouncedSearch,
    sortBy,
  });

  const handleViewDetails = (ad) => {
    setSelectedAd(ad);
    setShowDetailsModal(true);
  };

  useEffect(() => {
    document.title = 'Landing List | Vendor Ads Management';
  }, []);

  const headers = [
    {
      key: "id",
      title: "Sr. No",
    },
    {
      key: "userName",
      title: "User Name",
    },
    {
      key: "businessName",
      title: "Business Name",
    },
    {
      key: "section",
      title: "Section",
    },
    {
      key: "adSize",
      title: "Ad Size",
    },
    {
      key: "status",
      title: "Status",
    },
    {
      key: "createdAt",
      title: "Submitted",
    },
    {
      key: "actions",
      title: "Actions",
    },
  ];

  return (
    <>
      <DashboardLayout>
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-12">
              <div className="dashCard">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2 className="mainTitle">Vendor Ads</h2>
                </div>

                <div className="row mb-5">
                  <div className="col-12">
                    <div className="table-section-header mb-3">
                      <h4 className="mb-0">
                        <span className="badge bg-primary me-2">All Vendor Ads</span>
                        {data?.total || 0} ads
                      </h4>
                    </div>

                    {(isLoading || isFetching) ? <Loader /> :
                      <div className="col-12">
                        <CustomTable
                          headers={headers}
                          length={data?.data?.length || 0}
                          page={page}
                          rowsPerPage={perPage}
                          onNext={onNext}
                          onPrevious={onPrevious}
                          total={data?.total}
                        >
                          <tbody>
                            {(data?.data || []).map((item, index) => {
                              const statusMeta = getStatusMeta(item);

                              return (
                                <tr key={item._id}>
                                  <td>{(index + 1) + ((page - 1) * perPage)}</td>
                                  <td className="text-capitalize">
                                    <ProfileDisplay
                                      name={item?.vendor?.name || 'Unknown'}
                                      image={item?.vendor?.picture}
                                    />
                                  </td>
                                  <td>{item?.vendor?.details?.businessName || item?.businessName || 'N/A'}</td>
                                  <td>
                                    <span className="badge bg-info text-dark">
                                      {SECTION_LABELS[item?.displaySection] || item?.displaySection}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={`badge bg-${item?.adSize === 'gold' ? 'warning' : item?.adSize === 'silver' ? 'secondary' : item?.adSize === 'bronze' ? 'danger' : 'primary'}`}>
                                      {AD_SIZE_LABELS[item?.adSize] || item?.adSize}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={`badge ${statusMeta.className}`}>{statusMeta.label}</span>
                                  </td>
                                  <td>{dateFormatter(item?.createdAt)}</td>
                                  <td>
                                    <CustomButton
                                      variant="primaryButton"
                                      text=""
                                      className="btn-sm"
                                      onClick={() => handleViewDetails(item)}
                                    >
                                      <FontAwesomeIcon icon={faEye} />
                                    </CustomButton>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </CustomTable>
                      </div>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ad Details Modal */}
        {showDetailsModal && selectedAd && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Ad Details</h5>
                  <button type="button" className="btn-close" onClick={() => setShowDetailsModal(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="text-muted">Vendor Information</h6>
                      <p><strong>User Name:</strong> {selectedAd.vendor?.name || 'Unknown'}</p>
                      <p><strong>Business Name:</strong> {selectedAd.vendor?.details?.businessName || selectedAd.businessName || 'N/A'}</p>
                      <p><strong>Description:</strong> {selectedAd.businessInfo}</p>
                      <p><strong>Email:</strong> {selectedAd.contactEmail}</p>
                      <p><strong>Phone:</strong> {selectedAd.contactPhone || 'N/A'}</p>
                      <p><strong>Website:</strong> {selectedAd.website || 'N/A'}</p>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-muted">Ad Configuration</h6>
                      <p><strong>Section:</strong> {SECTION_LABELS[selectedAd.displaySection]}</p>
                      <p><strong>Size:</strong> {AD_SIZE_LABELS[selectedAd.adSize]}</p>
                    </div>
                  </div>
                  <hr />
                  <h6 className="text-muted">Ad Content</h6>
                  <p><strong>Headline:</strong> {selectedAd.adText?.headline}</p>
                  <p><strong>Description:</strong> {selectedAd.adText?.description}</p>
                  <p><strong>Call to Action:</strong> {selectedAd.adText?.callToAction}</p>
                  {selectedAd.adImages?.length > 0 && (
                    <>
                      <h6 className="text-muted mt-3">Images</h6>
                      <div className="d-flex gap-2 flex-wrap">
                        {selectedAd.adImages.map((img, idx) => (
                          <ImageViewer
                            key={idx}
                            src={img.url?.startsWith('http') ? img.url : `${process.env.REACT_APP_IMAGE_ENDPOINT || ''}${img.url}`}
                            image_style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px' }}
                            image_classname="me-2 mb-2"
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </DashboardLayout>
    </>
  );
};

export default VendorAds;
